import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import format from 'date-fns/format';
import ReactTooltip from 'react-tooltip';

import {
  Tab,
  Table,
  TableRow,
  TableCell,
  TabContent,
  Pagination,
  TableHeader,
  WrapperFlex,
} from '@unimed-fesp/components';

import { MdFileDownload } from 'react-icons/md';
import { FaFileDownload } from 'react-icons/fa';

import theme from '../../../styles/theme';

import {
  ContainerEmptyContent,
  HoverActions,
  DownloadMassaInicial,
  ContainerTabContent,
} from './styles';

import FadeIn from '../../atoms/FadeIn';

import LoadingSpinning from '../../molecules/LoadingSpinning';

const headers = [
  {
    name: 'Envio',
    minWidth: 150,
    maxWidth: 120,
  },
  {
    name: 'Empresa',
    minWidth: 300,
    maxWidth: 120,
  },
  {
    name: 'CNPJ',
    minWidth: 150,
    maxWidth: 120,
  },
  {
    name: 'Responsável',
    minWidth: 200,
    maxWidth: 120,
  },
  {
    name: 'E-mail',
    minWidth: 250,
    maxWidth: 120,
  },
  {
    name: 'Telefone',
    minWidth: 150,
    maxWidth: 120,
  },
  {
    name: 'Ações',
    minWidth: 100,
    maxWidth: 120,
  },
];

function TabTableListasMassaInicial({
  setPaginationParams,
  paginationParams,
  handleDropdownItem,
  handleDownloadFile,
  handleGetListaMassaInicial,
}) {
  const { getListaMassaInicial } = useSelector((state) => state.lista);

  const tabs = [
    {
      children: (
        <>
          <span>{getListaMassaInicial.response.Todas.size}</span>todas
        </>
      ),
      color: '#00995D',
      name: 'todas',
    },
  ];

  function handlePageChangeKeyDown(e) {
    if (e.keyCode === 13) {
      handleGetListaMassaInicial();
    }
  }

  function handleRender(listaStatus, tabName = null) {
    if (getListaMassaInicial.loading) {
      return <LoadingSpinning />;
    }
    if (listaStatus.data && listaStatus.data.length > 0) {
      const { data, ...pagination } = getListaMassaInicial.response[tabName];
      return (
        <TableLista
          pageData={listaStatus}
          listNameHeaders={headers}
          listas={data}
          setPaginationParams={setPaginationParams}
          paginationParams={pagination}
          handlePageChangeKeyDown={handlePageChangeKeyDown}
          handleDropdownItem={handleDropdownItem}
          handleDownloadFile={handleDownloadFile}
          {...(tabName && { tabName })}
        />
      );
    }
    return <EmptyContent />;
  }

  return (
    <>
      <Tab tabs={tabs}>
        <TabContent contentForTab="todas">
          {handleRender(getListaMassaInicial.response.Todas, 'Todas')}
        </TabContent>
      </Tab>
    </>
  );
}

const TableListaHeader = ({ listNameHeaders }) => {
  return (
    <TableRow>
      {listNameHeaders.map((header) => (
        <TableHeader minWidth={header.minWidth} maxWidth={header.maxWidth}>
          {header.name}
        </TableHeader>
      ))}
    </TableRow>
  );
};

const TableLista = ({
  listas,
  listNameHeaders,
  setPaginationParams,
  paginationParams,
  handlePageChangeKeyDown,
  handleDownloadFile,
  tabName,
  handleDropdownItem,
}) => {
  const [listaRowsChecked, setListaRowsChecked] = useState([]);

  return (
    <>
      <FadeIn height="auto">
        <ContainerTabContent>
          <Table head={<TableListaHeader listNameHeaders={listNameHeaders} />}>
            {listas.map((lista) => (
              <TableContent
                lista={lista}
                setListaRowsChecked={setListaRowsChecked}
                listaRowsChecked={listaRowsChecked}
                handleDownloadFile={handleDownloadFile}
                tabName={tabName}
                handleDropdownItem={handleDropdownItem}
              />
            ))}
          </Table>
        </ContainerTabContent>
      </FadeIn>
      {paginationParams.size > 10 && (
        <Pagination
          total={paginationParams.size}
          paginationParams={paginationParams}
          setPaginationParams={setPaginationParams}
          handlePageChangeKeyDown={handlePageChangeKeyDown}
        />
      )}
    </>
  );
};

const TableContent = ({ lista, handleDownloadFile }) => {
  const [hoverAction, setHoverAction] = useState(false);

  function handleMouseEnterHover(e) {
    setHoverAction(!hoverAction);
  }

  function handleMouseLeaveHover(e) {
    if (hoverAction) setHoverAction(!hoverAction);
  }

  function formatarCNPJ(cnpj) {
    const part1 = cnpj.substr(0, 2);
    const part2 = cnpj.substr(2, 3);
    const part3 = cnpj.substr(5, 3);
    const part4 = cnpj.substr(8, 4);
    const part5 = cnpj.substr(12, 2);

    return `${part1}.${part2}.${part3}/${part4}-${part5}`;
  }

  const dataInclusao = lista.dataInclusao
    ? format(new Date(lista.dataInclusao), 'dd/MM/yyyy HH:mm', {
        timeZone: 'America/Sao_Paulo',
      })
    : null;
  return (
    <TableRow
      onMouseEnter={handleMouseEnterHover}
      onMouseLeave={handleMouseLeaveHover}
      key={lista.idLista}
    >
      <TableCell>{dataInclusao}</TableCell>
      <TableCell>{lista.nmEmpresa}</TableCell>
      <TableCell>{formatarCNPJ(lista.cnpj)}</TableCell>
      <TableCell>{lista.nomeResponsavel}</TableCell>
      <TableCell>{lista.email}</TableCell>
      <TableCell>
        {lista.numero === 0 ? '-' : `(${lista.ddd}) ${lista.numero}`}
      </TableCell>
      <TableCell>
        <TableListaActions
          handleDownloadFile={(tipoAcao) => {
            handleDownloadFile(
              tipoAcao,
              lista.idListaMassaInicial,
              lista.idArquivo,
              `${tipoAcao}_${lista.cnpj}_${dataInclusao}`
            );
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const EmptyContent = () => (
  <WrapperFlex fullHeight justifyCenter alignCenter>
    <ContainerEmptyContent>Nenhum registro localizado</ContainerEmptyContent>
  </WrapperFlex>
);

const TableListaActions = ({ handleDownloadFile }) => {
  return (
    <WrapperFlex alignCenter justifySpaceEvenly>
      <DownloadMassaInicial
        onClick={() => handleDownloadFile('termo_responsabilidade')}
      >
        <HoverActions data-tip="Baixar termo de reponsabilidade">
          <FaFileDownload size="22" color={theme.colorGray} />
        </HoverActions>
      </DownloadMassaInicial>
      <DownloadMassaInicial onClick={() => handleDownloadFile('massa_inicial')}>
        <HoverActions data-tip="Baixar massa inicial">
          <MdFileDownload size="22" color={theme.colorGray} />
        </HoverActions>
      </DownloadMassaInicial>
      <ReactTooltip
        className="extraClass"
        delayHide={1000}
        effect="solid"
        backgroundColor={theme.greenDefault}
        textColor="#FFF"
        place="bottom"
      />
    </WrapperFlex>
  );
};

export default TabTableListasMassaInicial;
